// src/utils/passwordUtils.js

// Define character sets consistently
const charSets = {
  lowercase: 'abcdefghijklmnopqrstuvwxyz',
  uppercase: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ',
  digits: '0123456789',
  special: '!@#$%^&*()_+[]{}<>?|~`-=;\':"\\,./',
};

// Escape special characters in regex patterns
function escapeRegex(string) {
  return string.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
}

// Generate regex patterns from character sets
const charSetRegexes = {
  lowercase: new RegExp('[' + charSets.lowercase + ']'),
  uppercase: new RegExp('[' + charSets.uppercase + ']'),
  digits: new RegExp('[' + charSets.digits + ']'),
  special: new RegExp('[' + escapeRegex(charSets.special) + ']'),
};

// Generate a cryptographically secure password
export const generatePassword = ({ length, useUppercase, useDigits, useSpecial }) => {
  let charPool = charSets.lowercase;
  let requiredChars = [];

  if (useUppercase) {
    charPool += charSets.uppercase;
    requiredChars.push(randomCharFromSet(charSets.uppercase));
  }
  if (useDigits) {
    charPool += charSets.digits;
    requiredChars.push(randomCharFromSet(charSets.digits));
  }
  if (useSpecial) {
    charPool += charSets.special;
    requiredChars.push(randomCharFromSet(charSets.special));
  }

  // Calculate the remaining length
  const remainingLength = length - requiredChars.length;
  if (remainingLength < 0) {
    throw new Error('Password length too short for the selected character sets');
  }

  // Generate the rest of the password
  const passwordChars = requiredChars.slice();
  const randomValues = new Uint32Array(remainingLength);
  window.crypto.getRandomValues(randomValues);

  for (const value of randomValues) {
    const char = charPool[value % charPool.length];
    passwordChars.push(char);
  }

  // Shuffle the password characters
  shuffleArray(passwordChars);

  return passwordChars.join('');
};

// Helper function to get a random character from a set
function randomCharFromSet(charSet) {
  const randomValue = new Uint32Array(1);
  window.crypto.getRandomValues(randomValue);
  return charSet[randomValue[0] % charSet.length];
}

// Helper function to shuffle an array
function shuffleArray(array) {
  for (let i = array.length - 1; i > 0; i--) {
    const randomValue = new Uint32Array(1);
    window.crypto.getRandomValues(randomValue);
    const j = randomValue[0] % (i + 1);
    [array[i], array[j]] = [array[j], array[i]];
  }
}

// Calculate pool size dynamically based on password composition
const calculatePoolSize = (password) => {
  let poolSize = 0;
  for (const [key, regex] of Object.entries(charSetRegexes)) {
    if (regex.test(password)) {
      poolSize += charSets[key].length;
    }
  }
  return poolSize || 1; // Avoid division by zero
};

// Calculate diversity score
export const calculateDiversityScore = (password) => {
  let diversity = 0;
  for (const regex of Object.values(charSetRegexes)) {
    if (regex.test(password)) {
      diversity++;
    }
  }
  // Normalize the diversity score (0 to 1)
  return (diversity / Object.keys(charSets).length).toFixed(2);
};

// Calculate pattern-based penalties
const calculatePatternPenalties = (password) => {
  let penalty = 0;

  // Repeated characters (three or more in a row)
  const repeatedMatch = password.match(/(.)\1{2,}/g);
  if (repeatedMatch) {
    penalty += repeatedMatch.reduce((sum, match) => sum + match.length * 2, 0);
  }

  // Sequential characters (three or more sequential letters or numbers)
  const isSequential = (char1, char2, ascending = true) => {
    const diff = char1.charCodeAt(0) - char2.charCodeAt(0);
    return ascending ? diff === -1 : diff === 1;
  };

  for (let i = 0; i < password.length - 2; i++) {
    const seqAsc =
      isSequential(password[i], password[i + 1], true) &&
      isSequential(password[i + 1], password[i + 2], true);
    const seqDesc =
      isSequential(password[i], password[i + 1], false) &&
      isSequential(password[i + 1], password[i + 2], false);
    if (seqAsc || seqDesc) {
      penalty += 5; // Penalize sequences
    }
  }

  // Short passwords (less than 12 characters)
  if (password.length < 12) {
    penalty += (12 - password.length) * 2;
  }

  return penalty;
};

// Calculate entropy
export const calculateEntropy = (password) => {
  const poolSize = calculatePoolSize(password);
  let entropy = password.length * Math.log2(poolSize);

  // Subtract penalties
  const penalties = calculatePatternPenalties(password);
  entropy -= penalties;

  return Math.max(entropy, 0).toFixed(2); // Ensure no negative entropy
};

// Estimate time to crack
export const estimateTimeToCrack = (entropy) => {
  const guessesPerSecond = 1e10; // 10 billion guesses per second (modern GPUs)
  const timeInSeconds = Math.pow(2, entropy) / guessesPerSecond;

  return timeInSeconds;
};
